import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Loader2, Newspaper } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import api from "../lib/api";

const FEATURED_IMG =
  "https://images.unsplash.com/photo-1486286701208-1d58e9338013?crop=entropy&cs=srgb&fm=jpg&q=85&auto=format&fit=crop&w=1400";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const NewsSection = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api
      .get("/news")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const tags = useMemo(
    () => ["All", ...Array.from(new Set((items || []).map((n) => n.tag)))],
    [items]
  );
  const filtered = (items || []).filter((n) => filter === "All" || n.tag === filter);

  return (
    <section id="news" className="relative py-16 md:py-24 bg-paper/60 dark:bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>Headlines</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
            What's happening on <span className="font-serif italic font-medium marker-highlight">our patch</span>
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            News from around Morbi that matters to young football — written by me, from what
            people tell me.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter headlines by category">
            {tags.map((t) => (
              <button
                key={t}
                data-testid="news-category-filter"
                onClick={() => setFilter(t)}
                className={`px-4 py-2 rounded-full border-2 text-xs font-semibold uppercase tracking-wide transition-all ${
                  filter === t
                    ? "bg-pitch text-cream border-pitch dark:bg-turf dark:text-background dark:border-turf"
                    : "border-foreground/10 bg-card hover:border-pitch/40 dark:hover:border-turf/50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>

        {items === null ? (
          <div className="mt-10 grid place-items-center py-16 text-muted-foreground" data-testid="news-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="news-empty">
            <Newspaper className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-display font-bold text-lg">No headlines in this category yet</p>
            <p className="text-sm text-muted-foreground mt-1">I'm probably in class — check back soon.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6">
            {filtered.map((n, idx) => {
              const featured = idx === 0;
              const open = openId === n.id;
              return (
                <Reveal key={n.id} delay={0.04 * Math.min(idx, 4)}>
                  <article
                    data-testid="news-card"
                    className={`rounded-3xl bg-card border border-foreground/10 overflow-hidden shadow-sm ${
                      featured ? "md:grid md:grid-cols-2" : ""
                    }`}
                  >
                    {featured && (
                      <div className="relative h-52 md:h-full min-h-[220px] overflow-hidden">
                        <img src={FEATURED_IMG} alt="Floodlit stands on a match night" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-pitch/50 to-transparent" aria-hidden="true" />
                      </div>
                    )}
                    <div className="p-6 md:p-8">
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-flame-soft dark:bg-flame/15 text-flame px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-semibold">
                          {n.tag}
                        </span>
                        <span className="font-mono text-[11px] text-muted-foreground">{fmt(n.created_at)}</span>
                      </div>
                      <h3 className={`mt-3 font-display font-bold tracking-tight leading-snug ${featured ? "text-2xl sm:text-3xl" : "text-xl"}`}>
                        {n.title}
                      </h3>
                      <AnimatePresence initial={false}>
                        {open ? (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <p className="mt-3 text-muted-foreground leading-relaxed">{n.body}</p>
                          </motion.div>
                        ) : (
                          <p className="mt-3 text-muted-foreground leading-relaxed line-clamp-2">{n.body}</p>
                        )}
                      </AnimatePresence>
                      <button
                        data-testid="news-read-more"
                        onClick={() => setOpenId(open ? null : n.id)}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-pitch dark:text-turf hover:gap-2.5 transition-all"
                      >
                        {open ? "Show less" : "Read more"} <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
